"use client"

import { useState, useEffect, useRef } from "react"

import { Card, Tooltip, Popover, PopoverTrigger, PopoverContent } from "@heroui/react"
import { motion } from "framer-motion"

import { timelineEvents } from "@/app/data/events"
import type { TimelineEvent, TimelineEventStatus } from "@/lib/types"
import { DateTime } from "luxon"

export interface TimelineProps {
    events?: TimelineEvent[]
    debug?: boolean
    defaultSpacing?: number
}

// ─────────────────────────────────────────────
// Main Component
// ─────────────────────────────────────────────
export default function Timeline({
    events = timelineEvents,
    debug = false,
    defaultSpacing = 200,
}: TimelineProps) {
    const [debugProgress, setDebugProgress] = useState<number | null>(null)
    const [isMounted, setIsMounted] = useState(false)
    const [sessionNow, setSessionNow] = useState<number | null>(null)
    const [isVertical, setIsVertical] = useState(false)
    const [isTiny, setIsTiny] = useState(false)
    const [isTransitioning, setIsTransitioning] = useState(false)
    const containerRef = useRef<HTMLDivElement>(null)
    const wrapperRef = useRef<HTMLDivElement>(null)

    const spacings = events.map((e, i) => (i < events.length - 1 ? (e.spacingAfter ?? defaultSpacing) : 0))
    const nodeXPositions: number[] = []
    const SIDE_PAD = 40
    let acc = SIDE_PAD
    for (let i = 0; i < events.length; i++) {
        nodeXPositions.push(acc)
        acc += spacings[i]
    }
    const totalWidth = acc + SIDE_PAD
    const trackWidth = nodeXPositions[nodeXPositions.length - 1] - nodeXPositions[0]

    useEffect(() => {
        setIsMounted(true)
        setSessionNow(Date.now())

        if (!wrapperRef.current) return

        const observer = new ResizeObserver((entries) => {
            for (const entry of entries) {
                const newIsVertical = entry.contentRect.width < totalWidth + 150
                const newIsTiny = entry.contentRect.width < 400

                setIsTiny(newIsTiny)
                setIsVertical((prev) => {
                    if (prev !== newIsVertical) {
                        setIsTransitioning(true)
                        setTimeout(() => setIsTransitioning(false), 50)
                    }
                    return newIsVertical
                })
            }
        })

        observer.observe(wrapperRef.current)

        return () => {
            observer.disconnect()
        }
    }, [totalWidth])

    const filledWidth = computeFilledWidth(events, nodeXPositions, debugProgress, isMounted, sessionNow)
    const statuses = classifyEvents(events, nodeXPositions, filledWidth, isMounted)

    // Calculate the date to show on the progress tip
    let progressLabel = ""
    if (!isMounted || sessionNow === null) {
        progressLabel = "..."
    } else if (debugProgress === null) {
        const today = DateTime.now().toLocaleString(DateTime.DATE_FULL)
        progressLabel = `Today · ${today}`
    } else {
        const simulatedDate = computeDateAtWidth(events, nodeXPositions, filledWidth)
        progressLabel = simulatedDate.toFormat("yyyy LLLL dd")
    }

    // For debug slider display
    const visiblePercent = debugProgress !== null
        ? debugProgress
        : (filledWidth / trackWidth) * 100

    return (
        <>
            <style>{`
                @keyframes timeline-pulse {
                    0%, 100% { opacity: 0.35; transform: scale(1); }
                    50%       { opacity: 0;    transform: scale(1.9); }
                }
            `}</style>
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
            >
                <div ref={wrapperRef} className="w-full flex flex-col items-center">
                    <h2 className="second-header pb-6">AUDITION TIMELINE</h2>
                    <Card className={`bg-transparent backdrop-blur-2xl outline-1 outline-black rounded-2xl purple-glow select-none transition-all ${isVertical ? "min-w-[250px] w-full max-w-sm" : "w-fit mx-auto"}`}>
                        {debug && (
                            <div className="mb-6 mx-auto max-w-xl rounded-xl border border-primary-700/40 bg-white/5 p-4">
                                <div className="flex items-center justify-between mb-2">
                                    <span className="text-xs font-bold text-primary-300 uppercase tracking-widest">
                                        🛠 Debug: Timeline Progress
                                    </span>
                                    <button
                                        onClick={() => setDebugProgress(null)}
                                        className="text-[10px] text-white/30 hover:text-white/60 transition-colors"
                                    >
                                        Reset to date
                                    </button>
                                </div>
                                <input
                                    type="range"
                                    min={0}
                                    max={100}
                                    step={0.5}
                                    value={visiblePercent}
                                    onChange={(e) => setDebugProgress(parseFloat(e.target.value))}
                                    className="w-full accent-primary-400 cursor-pointer"
                                />
                                <div className="flex justify-between text-[10px] text-white/30 mt-1">
                                    <span>0%</span>
                                    <span className="text-primary-300 font-semibold">{visiblePercent.toFixed(1)}%</span>
                                    <span>100%</span>
                                </div>
                            </div>
                        )}

                        <div className={`pb-8 pt-4 flex items-center justify-center w-full ${isVertical ? "min-h-[400px] py-16 px-4" : "min-h-64 py-32 px-24"}`}>
                            <div
                                ref={containerRef}
                                className={`relative w-full ${isVertical ? "min-w-[300px] h-(--total-len)" : "w-(--total-len) min-w-(--total-len) h-0"}`}
                                style={{ "--total-len": `${totalWidth}px` } as React.CSSProperties}
                            >
                                {/* Background track */}
                                <div
                                    className={`absolute rounded-full bg-white/10 ${isVertical
                                        ? "left-1/2 -translate-x-1/2 top-(--start-pos) w-[3px] h-(--track-len)"
                                        : "top-1/2 -translate-y-1/2 left-(--start-pos) h-[3px] w-(--track-len)"
                                        }`}
                                    style={{
                                        "--start-pos": `${nodeXPositions[0]}px`,
                                        "--track-len": `${trackWidth}px`
                                    } as React.CSSProperties}
                                />

                                {/* Filled progress bar */}
                                <div
                                    className={`absolute rounded-full ease-out z-10 
                                    ${isTransitioning ? "duration-0" : "duration-700 transition-[height]"}
                                    ${isVertical
                                            ? "left-1/2 -translate-x-1/2 top-(--start-pos) w-[3px] h-(--filled-len) bg-linear-to-b"
                                            : "top-1/2 -translate-y-1/2 left-(--start-pos) h-[3px] w-(--filled-len) bg-linear-to-r"
                                        } from-primary-700 via-primary-300 via-60% to-primary-100`}
                                    style={{
                                        "--start-pos": `${nodeXPositions[0]}px`,
                                        "--filled-len": `${filledWidth}px`,
                                        boxShadow: "0 0 10px 2px rgba(139,90,230,0.5)",
                                    } as React.CSSProperties}
                                />

                                {events.map((event, i) => (
                                    <div
                                        key={`label-${event.id}`}
                                        className={`absolute -translate-x-1/2 -translate-y-1/2 z-0 ${isVertical
                                            ? "left-1/2 top-(--node-pos)"
                                            : "top-1/2 left-(--node-pos)"
                                            }`}
                                        style={{ "--node-pos": `${nodeXPositions[i]}px` } as React.CSSProperties}
                                    >
                                        <TimelineLabels event={event} status={statuses[i]} index={i} isVertical={isVertical} />
                                    </div>
                                ))}

                                {/* Progress tip */}
                                {filledWidth > 0 && filledWidth < trackWidth && (
                                    <ProgressTip pos={nodeXPositions[0] + filledWidth} label={progressLabel} isVertical={isVertical} />
                                )}

                                {events.map((event, i) => (
                                    <div
                                        key={`dot-${event.id}`}
                                        className={`absolute -translate-x-1/2 -translate-y-1/2 z-30 ${isVertical
                                            ? "left-1/2 top-(--node-pos)"
                                            : "top-1/2 left-(--node-pos)"
                                            }`}
                                        style={{ "--node-pos": `${nodeXPositions[i]}px` } as React.CSSProperties}
                                    >
                                        <TimelineInteractiveNode event={event} status={statuses[i]} index={i} isVertical={isVertical} isTiny={isTiny} />
                                    </div>
                                ))}
                            </div>
                        </div>
                    </Card>
                </div>
            </motion.div>
        </>
    )
}

// ─────────────────────────────────────────────
// Subcomponents
// ─────────────────────────────────────────────

interface NodeProps {
    event: TimelineEvent
    status: TimelineEventStatus
    index: number
    isVertical: boolean
    isTiny?: boolean
}

function TimelineLabels({ event, status, index, isVertical }: NodeProps) {
    const isPast = status === "past"
    const isCurrent = status === "current"
    const labelAbove = index % 2 === 0

    return (
        <div className={`relative flex items-center pointer-events-none ${isVertical ? "flex-row" : "flex-col"}`}>
            {/* Slot A (Mobile Left / Desktop Top) */}
            <div className={`
                flex flex-col 
                ${isVertical
                    ? "justify-center items-end w-28 pr-4 text-right"
                    : "justify-end items-center w-auto h-24 pb-3 text-center"
                }
                ${labelAbove ? "opacity-100" : "opacity-0 text-transparent"}
            `}>
                <span className={`text-xs font-semibold text-primary-200/80 ${!isVertical ? "whitespace-nowrap" : ""}`}>{event.date.toFormat("yyyy LLLL dd")}</span>
                <span className={`text-sm font-bold transition-colors duration-300 ${!isVertical ? "whitespace-nowrap" : ""} ${isCurrent ? "text-primary-100" : isPast ? "text-white/70" : "text-white/30"}`}>
                    {event.title}
                </span>
            </div>

            <div className="w-5 h-5 shrink-0" />

            <div className={`
                flex flex-col 
                ${isVertical
                    ? "justify-center items-start w-28 pl-4 text-left"
                    : "justify-start items-center w-auto h-24 pt-3 text-center"
                }
                ${!labelAbove ? "opacity-100" : "opacity-0 text-transparent"}
            `}>
                <span className={`text-xs font-semibold text-primary-200/80 ${!isVertical ? "whitespace-nowrap" : ""}`}>{event.date.toFormat("yyyy LLLL dd")}</span>
                <span className={`text-sm font-bold transition-colors duration-300 ${!isVertical ? "whitespace-nowrap" : ""} ${isCurrent ? "text-primary-100" : isPast ? "text-white/70" : "text-white/30"}`}>
                    {event.title}
                </span>
            </div>
        </div>
    )
}

function TimelineInteractiveNode({ event, status, index, isVertical, isTiny }: NodeProps) {
    const isPast = status === "past"
    const isCurrent = status === "current"

    const ringColor = isPast ? "border-primary-500" : isCurrent ? "border-primary-300" : "border-white/20"
    const dotBg = isPast ? "bg-primary-500" : isCurrent ? "bg-primary-300" : "bg-white/10"
    const glowStyle: React.CSSProperties = isCurrent
        ? { boxShadow: "0 0 18px 4px rgba(153,112,229,0.7)" }
        : isPast
            ? { boxShadow: "0 0 10px 2px rgba(106,46,217,0.5)" }
            : {}

    const labelAbove = index % 2 === 0

    return (
        <Popover
            placement={isTiny ? (labelAbove ? "top" : "bottom") : (isVertical ? (labelAbove ? "right" : "left") : (labelAbove ? "bottom" : "top"))}
            classNames={{
                base: "bg-transparent border-none shadow-none p-0 before:hidden",
                content: "bg-transparent shadow-none p-0",
            }}
        >
            <PopoverTrigger>
                <div
                    role="button"
                    tabIndex={0}
                    aria-label={`View details for ${event.title}`}
                    className="relative flex items-center justify-center cursor-pointer hover:scale-150 transition-all duration-300 ease-in-out"
                >
                    {isCurrent && (
                        <span
                            className="absolute rounded-full"
                            style={{
                                inset: "-6px",
                                background: "rgba(153,112,229,0.35)",
                                animation: "timeline-pulse 2.4s ease-in-out infinite",
                            }}
                        />
                    )}
                    <div
                        className={`w-5 h-5 rounded-full border-2 ${ringColor} ${dotBg} z-30 transition-colors duration-300`}
                        style={glowStyle}
                    />
                </div>
            </PopoverTrigger>
            <PopoverContent>
                <NodeTooltipContent event={event} />
            </PopoverContent>
        </Popover>
    )
}

function ProgressTip({ pos, label, isVertical }: { pos: number; label: string; isVertical: boolean }) {
    return (
        <div
            className={`absolute -translate-x-1/2 -translate-y-1/2 z-10 ${isVertical ? "left-1/2 top-(--pos)" : "top-1/2 left-(--pos)"
                }`}
            style={{ "--pos": `${pos}px` } as React.CSSProperties}
        >
            <Tooltip
                content={
                    <div className="rounded-lg border border-primary-600/50 bg-primary-1000/95 backdrop-blur-md px-2.5 py-1.5 shadow-xl">
                        <span className="text-[11px] font-semibold text-primary-200">{label}</span>
                    </div>
                }
                placement="top"
                classNames={{
                    base: "bg-transparent border-none shadow-none p-0",
                    content: "bg-transparent shadow-none p-0",
                }}
            >
                <div
                    className="w-3 h-3 rounded-full bg-primary-200 cursor-default"
                    style={{ boxShadow: "0 0 14px 4px rgba(153,112,229,0.9)" }}
                />
            </Tooltip>
        </div>
    )
}

function NodeTooltipContent({ event }: { event: TimelineEvent }) {
    return (
        <div className="rounded-xl border border-primary-700/60 bg-primary-1000/95 backdrop-blur-md p-4 shadow-2xl w-56">
            <p className="text-xs text-primary-300 font-semibold mb-0.5">{event.date.toFormat("yyyy LLLL dd")}</p>
            <p className="text-sm font-bold text-primary-100 mb-1">{event.title}</p>
            <p className="text-xs text-white/60 leading-relaxed whitespace-pre-line">{event.description}</p>
        </div>
    )
}

// ─────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────

/**
 * Returns a pixel width for the filled progress bar, mapping the current
 * time onto the custom physical X positions of the nodes.
 */
function computeFilledWidth(
    events: TimelineEvent[],
    nodeXPositions: number[],
    debugProgress: number | null,
    isMounted: boolean = true,
    sessionTime: number | null = null
): number {
    if (events.length === 0 || nodeXPositions.length === 0) return 0

    // For manual debugging
    const trackWidth = nodeXPositions[nodeXPositions.length - 1] - nodeXPositions[0]
    if (debugProgress !== null) {
        return (debugProgress / 100) * trackWidth
    }

    if (!isMounted || sessionTime === null) return 0

    const now = sessionTime
    const start = events[0].date.toMillis()
    const end = events[events.length - 1].date.toMillis()

    if (now <= start) return 0
    if (now >= end) return trackWidth

    // Find which two nodes 'now' is currently between
    for (let i = 0; i < events.length - 1; i++) {
        const d1 = events[i].date.toMillis()
        const d2 = events[i + 1].date.toMillis()

        if (now >= d1 && now <= d2) {
            const timeRange = d2 - d1
            if (timeRange === 0) return nodeXPositions[i] - nodeXPositions[0]

            const segmentProgress = (now - d1) / timeRange
            const physicalRange = nodeXPositions[i + 1] - nodeXPositions[i]
            const physicalProgress = segmentProgress * physicalRange

            return (nodeXPositions[i] - nodeXPositions[0]) + physicalProgress
        }
    }
    return 0
}

/**
 * Reverse-maps a physical pixel width back to a Date.
 */
function computeDateAtWidth(
    events: TimelineEvent[],
    nodeXPositions: number[],
    filledWidth: number
): DateTime {
    if (events.length === 0) return DateTime.now();
    const trackWidth = nodeXPositions[nodeXPositions.length - 1] - nodeXPositions[0]

    if (filledWidth <= 0) return events[0].date
    if (filledWidth >= trackWidth) return events[events.length - 1].date

    for (let i = 0; i < nodeXPositions.length - 1; i++) {
        const x1 = nodeXPositions[i] - nodeXPositions[0]
        const x2 = nodeXPositions[i + 1] - nodeXPositions[0]

        if (filledWidth >= x1 && filledWidth <= x2) {
            const segmentPhysRange = x2 - x1
            if (segmentPhysRange === 0) return events[i].date

            const segmentProgress = (filledWidth - x1) / segmentPhysRange
            const t1 = events[i].date.toMillis()
            const t2 = events[i + 1].date.toMillis()

            return DateTime.fromMillis(t1 + (t2 - t1) * segmentProgress);
        }
    }
    return events[events.length - 1].date
}

function classifyEvents(
    events: TimelineEvent[],
    nodeXPositions: number[],
    filledWidth: number,
    isMounted: boolean = true
): TimelineEventStatus[] {
    if (events.length === 0) return []

    if (!isMounted) {
        return events.map((_, i) => (i === 0 ? "current" : "future"))
    }

    // Identify which segment we are in physically
    let activeIdx = 0
    const trackWidth = nodeXPositions[nodeXPositions.length - 1] - nodeXPositions[0]

    if (filledWidth >= trackWidth) {
        activeIdx = events.length - 1
    } else {
        for (let i = 0; i < nodeXPositions.length - 1; i++) {
            const xLeft = nodeXPositions[i] - nodeXPositions[0]
            const xRight = nodeXPositions[i + 1] - nodeXPositions[0]
            if (filledWidth >= xLeft && filledWidth < xRight) {
                activeIdx = i
                break
            }
        }
    }

    return events.map((_, i) => {
        if (i < activeIdx) return "past"
        if (i === activeIdx) return "current"
        return "future"
    })
}